import React from "react";
import { Check } from "lucide-react";
import { cn } from "lib/utils";

interface ChoiceCardProps {
  selected: boolean;
  onSelect: () => void;
  title: React.ReactNode;
  hint?: React.ReactNode;
  icon?: React.ReactNode;
  /** "radio" for one-of choices (the default), "checkbox" for pick-any lists. */
  kind?: "radio" | "checkbox";
  className?: string;
}

/** A selectable card with a tick, in the same style as the ready-time choice on Add Product. */
export default function ChoiceCard({ selected, onSelect, title, hint, icon, kind = "radio", className }: ChoiceCardProps) {
  return (
    <button
      type="button"
      role={kind}
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        "flex w-full items-start gap-3 rounded-[14px] border p-4 text-start transition-colors",
        selected ? "border-default-900 bg-card" : "border-default-200 bg-default-50 hover:border-default-400",
        className,
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex h-5 w-5 flex-none items-center justify-center border",
          kind === "radio" ? "rounded-full" : "rounded-[6px]",
          selected ? "border-default-900 bg-default-900 text-default-foreground" : "border-default-300 bg-card",
        )}
      >
        {selected && <Check className="h-3 w-3" strokeWidth={3} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 text-sm font-medium text-default-900">
          {icon}
          {title}
        </span>
        {hint && <span className="mt-0.5 block text-xs text-default-600">{hint}</span>}
      </span>
    </button>
  );
}
